// src/components/dashboards/charts/MutationPrevalenceBar.tsx
// Grouped bar chart: mutation gene prevalence (positive / negative / unknown).

import React from "react";
import { Box, Skeleton, Typography, alpha } from "@mui/material";
import ReactECharts from "echarts-for-react";
import useChartTheme from "./useChartTheme";
import { athenaColors } from "../../../theme/palette";
import { MutationsData } from "hooks/useAnalytics";
import { useTranslation } from "../../../i18n";

interface Props {
  data: MutationsData | null;
  loading: boolean;
}

const MutationPrevalenceBar: React.FC<Props> = ({ data, loading }) => {
  const { baseAxis, baseTooltip, baseGrid, textPrimary, theme, chartHeight, dark } = useChartTheme();
  const { t } = useTranslation();

  if (loading || !data) {
    return <Skeleton variant="rounded" height={chartHeight + 20} sx={{ borderRadius: 2 }} />;
  }

  const mutations = data.mutations.filter(
    (m) => m.positive + m.negative + m.unknown > 0
  );
  if (mutations.length === 0) {
    return (
      <Box sx={{ height: chartHeight + 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {t("dashboard.analytics.no_mutation_data", "No mutation data available")}
        </Typography>
      </Box>
    );
  }

  const genes = mutations.map((m) => m.gene);

  const option = {
    title: {
      text: t("dashboard.analytics.mutation_prevalence", "Mutation Prevalence"),
      textStyle: { color: textPrimary, fontSize: 14, fontWeight: 600 },
      left: 0,
      top: 0,
    },
    tooltip: { ...baseTooltip, trigger: "axis", axisPointer: { type: "shadow" } },
    legend: {
      data: ["Positive", "Negative", "Unknown"],
      textStyle: { color: textPrimary, fontSize: 11 },
      top: 28,
      right: 0,
    },
    grid: { ...baseGrid, top: "22%" },
    xAxis: { ...baseAxis, type: "category", data: genes, axisLabel: { ...baseAxis.axisLabel, rotate: genes.length > 8 ? 35 : 0 } },
    yAxis: { ...baseAxis, type: "value" },
    series: [
      {
        name: "Positive",
        type: "bar",
        stack: "total",
        data: mutations.map((m) => m.positive),
        itemStyle: {
          color: dark ? athenaColors.orange : theme.palette.error.main,
          borderRadius: 0,
        },
        barMaxWidth: 28,
      },
      {
        name: "Negative",
        type: "bar",
        stack: "total",
        data: mutations.map((m) => m.negative),
        itemStyle: {
          color: dark ? athenaColors.periwinkle : theme.palette.success.main,
        },
        barMaxWidth: 28,
      },
      {
        name: "Unknown",
        type: "bar",
        stack: "total",
        data: mutations.map((m) => m.unknown),
        itemStyle: {
          color: dark
            ? alpha(theme.palette.common.white, 0.16)
            : theme.palette.grey[500],
          borderRadius: [4, 4, 0, 0],
        },
        barMaxWidth: 28,
      },
    ],
  };

  return (
    <Box sx={{ width: "100%", height: chartHeight + 20 }}>
      <ReactECharts option={option} style={{ width: "100%", height: "100%" }} opts={{ renderer: "canvas" }} />
    </Box>
  );
};

export default MutationPrevalenceBar;
